// components/BeneficiaireModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ClipboardIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

//https://cloterenotaire.onrender.com
//http://localhost:5050

export default function BeneficiaireModal({
  open,
  onClose,
  id_action, // string OU array
  apiBase = API_BASE,
  title = "Bénéficiaires",
}) {
  const [loading, setLoading] = useState(false);
  const [donataires, setDonataires] = useState([]);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  // --------------------------------------------------
  // FETCH API
  // --------------------------------------------------
  useEffect(() => {
    if (!open || !id_action) return;

    const ids = Array.isArray(id_action) ? id_action : [id_action];
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${apiBase}/api/getBeneficiairesByAction`, {
          params: { id_action: ids },
          paramsSerializer: { indexes: null },
        });

        if (cancelled) return;

        let payload = null;

        // ✅ CAS 1 : l’API renvoie directement { donataires: [...] }
        if (res.data?.donataires) {
          payload = res.data;
        }
        // ✅ CAS 2 : l’API renvoie { record: { payload_json } }
        else if (res.data?.record?.payload_json) {
          payload =
            typeof res.data.record.payload_json === "string"
              ? JSON.parse(res.data.record.payload_json)
              : res.data.record.payload_json;
        }

        if (!payload?.donataires || payload.donataires.length === 0) {
          setDonataires([]);
          return;
        }

        setDonataires(payload.donataires);
      } catch (err) {
        console.error("❌ fetch beneficiaires:", err);
        setError("Impossible de charger les bénéficiaires");
        setDonataires([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, id_action, apiBase]);

  // --------------------------------------------------
  // COPY
  // --------------------------------------------------
  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(field);
      setTimeout(() => setCopied(null), 1200);
    } catch (err) {
      console.error("copy error:", err);
    }
  };

  const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
  const formatLabel = (key) =>
    String(key)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // --------------------------------------------------
  // EXPORT CSV
  // --------------------------------------------------
  function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toCsv(rows) {
    const esc = (s) => {
      const v = String(s ?? "");
      return /[;\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const lines = [
      ["Bénéficiaire", "Champ", "Valeur"].join(";"),
      ...rows.map(({ idx, label, value }) =>
        [esc(`Bénéficiaire ${idx}`), esc(label), esc(value)].join(";")
      ),
    ];
    return lines.join("\n");
  }

  const csvRows = useMemo(() => {
    if (!donataires?.length) return [];
    const out = [];
    donataires.forEach((benef, i) => {
      Object.entries(benef || {}).forEach(([k, v]) => {
        if (!hasValue(v)) return;
        out.push({ idx: i + 1, label: formatLabel(k), value: String(v) });
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donataires]);

  const handleExportCsv = () => {
    const csv = toCsv(csvRows);
    const suffix = Array.isArray(id_action) ? `_${id_action.join("-")}` : id_action ? `_${id_action}` : "";
    const now = new Date();
    const iso = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
    ].join("");
    downloadBlob(csv, "text/csv;charset=utf-8", `beneficiaires${suffix}_${iso}.csv`);
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {title}
              </DialogTitle>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={loading || !donataires?.length}
                className="btn-primary"
                title="Exporter en CSV"
              >
                Export CSV
              </button>

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
                title="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="rounded-md bg-slate-50 p-4 mb-3">
                <p className="text-sm text-slate-700 m-0">Chargement…</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-3">
                <p className="text-sm text-red-700 m-0">{error}</p>
              </div>
            )}

            {!loading && !error && (!donataires || donataires.length === 0) && (
              <div className="rounded-md bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 m-0">Aucun bénéficiaire renseigné.</p>
              </div>
            )}

            {!loading && !error && donataires?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {donataires.map((benef, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white"
                  >
                    <div className="bg-gray-50 px-4 py-2 border-b">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Bénéficiaire {index + 1}
                      </h3>
                    </div>

                    <dl className="divide-y divide-gray-200">
                      {Object.entries(benef || {}).map(([key, value]) => (
                        <div
                          key={key}
                          className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-4 py-3 items-start"
                        >
                          {/* LABEL */}
                          <dt className="sm:col-span-4 text-sm font-medium text-gray-700">
                            {formatLabel(key)}
                          </dt>

                          {/* VALUE */}
                          <dd className="sm:col-span-7 text-sm text-black font-bold whitespace-pre-wrap break-words">
                            {hasValue(value) ? (
                              String(value)
                            ) : (
                              <span className="font-normal text-gray-400">
                                Non renseigné
                              </span>
                            )}
                          </dd>

                          {/* COPY BTN */}
                          <div className="sm:col-span-1 flex sm:justify-end">
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(`${index}-${key}`, String(value ?? ""))
                              }
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              title="Copier"
                            >
                              {copied === `${index}-${key}` ? (
                                <>
                                  <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                                  Copié
                                </>
                              ) : (
                                <>
                                  <ClipboardIcon className="h-4 w-4 text-gray-500" />
                                  Copier
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}